import { useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, ChevronRight, Edit3, FileText, GraduationCap, Pencil, Phone, Plus, Search, Trash2, UserPlus, Users, XCircle, Clock, PhoneCall, AlertTriangle, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/form-inputs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { getModuleConfig, getModuleStudentPath, type ModuleKey } from '../../lib/moduleConfig';

const CLASS_NAMES: Record<string, string> = {
  '1': 'Class 6', '2': 'Class 7', '3': 'Class 8',
  '4': 'Class 9', '5': 'Class 10', '6': 'Class 11', '7': 'Class 12',
};

  const statusConfig: Record<string, { label: string; color: string; bg: string; icon: any }> = {
    active: { label: 'Active', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/20', icon: CheckCircle },
    drop: { label: 'Dropped', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-100 dark:bg-rose-500/20', icon: XCircle },
    enquiry: { label: 'Enquiry', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/20', icon: Clock },
    left: { label: 'Drop', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-100 dark:bg-rose-500/20', icon: XCircle },
  };

function EnquiryForm({ initial, classId, batchId, onSave, onCancel }: any) {
  const [form, setForm] = useState({
    studentName: initial?.student_name || '',
    phone: initial?.phone || '',
    guardianName: initial?.guardian_name || '',
    notes: initial?.notes || '',
    status: initial?.status || 'new',
  });
  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 p-3 space-y-2">
      <input className="w-full h-11 rounded-xl px-3 text-sm bg-white dark:bg-dark-elevated border border-light-border dark:border-dark-border" placeholder="Student name" value={form.studentName} onChange={(e) => setForm({ ...form, studentName: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        <input className="w-full h-11 rounded-xl px-3 text-sm bg-white dark:bg-dark-elevated border border-light-border dark:border-dark-border" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <input className="w-full h-11 rounded-xl px-3 text-sm bg-white dark:bg-dark-elevated border border-light-border dark:border-dark-border" placeholder="Guardian" value={form.guardianName} onChange={(e) => setForm({ ...form, guardianName: e.target.value })} />
      </div>
      <textarea className="w-full min-h-20 rounded-xl px-3 py-2 text-sm bg-white dark:bg-dark-elevated border border-light-border dark:border-dark-border" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave({ ...form, classId: Number(classId), batchId: batchId ? Number(batchId) : undefined })}>Save enquiry</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function ModuleStudentListPage() {
  const { module, classId, batchId, status } = useParams();
  const moduleKey = (module || 'students') as ModuleKey;
  const config = getModuleConfig(module);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEnquiries = location.pathname.endsWith('/enquiries');
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callNumbers, setCallNumbers] = useState<Array<{ label: string; phone?: string; sub?: string }>>([]);
  const [enquiryDeleteId, setEnquiryDeleteId] = useState<number | null>(null);

  const displayClassName = CLASS_NAMES[classId || ''] || `Class ${classId}`;
  const title = isEnquiries ? 'Enquiries' : status === 'drop' ? 'Dropped' : 'Students';
  const queryKey = isEnquiries ? ['module-enquiries', classId, batchId] : ['module-students', classId, batchId, status];

  const { data: rowsRaw, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (isEnquiries) {
        const params = new URLSearchParams();
        if (classId) params.set('classId', classId);
        if (batchId && batchId !== 'total') params.set('batchId', batchId);
        const res = await api.get<any>(`/api/enquiries?${params.toString()}`);
        return res.data || [];
      }
      const params = new URLSearchParams({ pageSize: '500' });
      if (classId) params.set('classId', classId);
      if (batchId && batchId !== 'total') params.set('batchId', batchId);
      if (status) params.set('status', status);
      const res = await api.get<any>(`/api/students?${params.toString()}`);
      return res.data?.items || res.data || [];
    },
  });

  // Module-specific filtering:
  // - Always hide pending (super admin bulk import only)
  // - exams: hide dropped and enquiry
  // - attendance: hide enquiry
  const rows = isEnquiries ? (rowsRaw || []) : (() => {
    if (!rowsRaw) return [];
    const base = rowsRaw.filter((row: any) => row.status !== 'pending');
    if (moduleKey === 'exams') {
      return base.filter((row: any) => row.status !== 'drop' && row.status !== 'left' && row.status !== 'enquiry');
    }
    if (moduleKey === 'attendance') {
      return base.filter((row: any) => row.status !== 'enquiry');
    }
    return base;
  })();

  const { data: invoicesData } = useQuery({
    queryKey: ['module-payment-invoices', classId, batchId],
    queryFn: async () => {
      const res = await api.get<any>('/api/fees/invoices?pageSize=500');
      return res.data || [];
    },
    enabled: moduleKey === 'payments' && !isEnquiries,
  });

  const paymentStatusMap = useMemo(() => {
    const map: Record<number, { status: string; due: number; paid: number }> = {};
    if (Array.isArray(invoicesData)) {
      for (const inv of invoicesData) {
        const sid = inv.student_id;
        if (!map[sid] || inv.status === 'due' || inv.status === 'overdue') {
          map[sid] = { status: inv.status, due: inv.due_amount || 0, paid: inv.paid_amount || 0 };
        }
      }
    }
    return map;
  }, [invoicesData]);

  const paymentStatusConfig: Record<string, { label: string; color: string; bg: string; icon: any }> = {
    paid: { label: 'Paid', color: 'text-lime-600 dark:text-lime-400', bg: 'bg-lime-100 dark:bg-lime-500/20', icon: CheckCircle },
    partial: { label: 'Partial', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/20', icon: Clock },
    due: { label: 'Due', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-100 dark:bg-rose-500/20', icon: AlertTriangle },
    overdue: { label: 'Overdue', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-100 dark:bg-rose-500/20', icon: AlertTriangle },
  };

  const saveEnquiry = useMutation({
    mutationFn: async (payload: any) => editingId ? api.patch(`/api/enquiries/${editingId}`, payload) : api.post('/api/enquiries', payload),
    onSuccess: () => {
      toast.success(editingId ? 'Enquiry updated' : 'Enquiry added');
      setShowAdd(false);
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to save enquiry'),
  });

  const deleteEnquiry = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/enquiries/${id}`),
    onSuccess: () => { toast.success('Enquiry deleted'); queryClient.invalidateQueries({ queryKey }); },
  });

  const deleteStudent = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/students/${id}`),
    onSuccess: () => {
      toast.success('Student deleted');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && (key === 'module-students' || key === 'students' || key === 'dashboard-summary');
      }});
    },
    onError: (err: any) => toast.error(err.message || 'Failed to delete student'),
  });

  const convertEnquiry = useMutation({
    mutationFn: async (id: number) => api.post<any>(`/api/enquiries/${id}/convert`, {}),
    onSuccess: (res) => {
      toast.success('Converted to student');
      queryClient.invalidateQueries({ queryKey });
      if (res.data?.studentId) navigate(`/students/${res.data.studentId}`);
    },
    onError: (err: any) => toast.error(err.message || 'Failed to convert enquiry'),
  });

  return (
    <div className="space-y-4 pb-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/modules/${moduleKey}/classes/${classId}`)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
              <config.icon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
              {title}
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1 ml-10">
            {config.label} / {displayClassName}
            {batchId && (() => {
              const batch = (rows || []).find((r: any) => r.batch_id === Number(batchId));
              return batch ? ` / ${batch.batch_name || 'Batch'}` : '';
            })()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {getTopButton()}
        </div>
      </div>

      {showAdd && <EnquiryForm classId={classId} batchId={batchId} onSave={(payload: any) => saveEnquiry.mutate(payload)} onCancel={() => setShowAdd(false)} />}

      {/* Student Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-40 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />
          ))}
        </div>
      ) : rows?.length === 0 ? (
        <div className="rounded-xl border border-dashed border-light-border dark:border-dark-border p-12 text-center">
          <Users className="w-12 h-12 text-light-text-muted mx-auto mb-3" />
          <p className="text-light-text-muted">No records found here yet.</p>
        </div>
      ) : isEnquiries ? (
        <div className="space-y-2">
          {rows.map((row: any, index: number) => {
            if (editingId === row.id) {
              return <EnquiryForm key={row.id} initial={row} classId={classId} batchId={batchId} onSave={(payload: any) => saveEnquiry.mutate(payload)} onCancel={() => setEditingId(null)} />;
            }
            return (
              <div key={row.id} className="rounded-xl bg-white dark:bg-dark-surface border border-light-border dark:border-dark-border p-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-light-text-muted font-mono">#{index + 1}</span>
                      <p className="font-bold text-light-text dark:text-dark-text truncate">{row.student_name}</p>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-100 whitespace-nowrap">{row.status}</span>
                    </div>
                    <p className="text-xs text-light-text-muted truncate mt-0.5">{row.phone || 'No phone'} {row.guardian_name ? `• ${row.guardian_name}` : ''}</p>
                    {row.notes && <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">{row.notes}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="secondary" className="h-7 text-[10px] gap-1" onClick={() => convertEnquiry.mutate(row.id)}><CheckCircle className="w-3 h-3" /> Convert</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-[10px] gap-1" onClick={() => setEditingId(row.id)}><Edit3 className="w-3 h-3" /> Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-[10px] gap-1 text-rose-600" onClick={() => setEnquiryDeleteId(row.id)}><Trash2 className="w-3 h-3" /> Delete</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
          <div className="rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
          {/* Table header */}
          <div className="flex items-center gap-1 px-3 py-2 bg-light-elevated dark:bg-dark-elevated border-b border-light-border dark:border-dark-border text-[10px] font-semibold text-light-text-muted">
            <div className="w-10 text-center">Roll</div>
            <div className="flex-1 min-w-0 pl-1">Name</div>
            <div className="w-[68px] text-center shrink-0">Status</div>
            <div className="w-[68px] shrink-0"></div>
          </div>

           <div className="divide-y divide-light-border dark:divide-dark-border">
            {(rows || []).map((student: any, index: number) => {
              const statusInfo = statusConfig[student.status] || statusConfig.active;
              const StatusIcon = statusInfo.icon;

              return (
                <motion.div
                  key={student.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(index * 0.01, 0.3) }}
                  onClick={() => navigate(getModuleStudentPath(moduleKey, student.id, classId, batchId))}
                  className="flex items-center gap-1 px-3 py-2 hover:bg-light-elevated/50 dark:hover:bg-dark-elevated/50 transition-colors cursor-pointer"
                >
                  {/* Roll */}
                  <div className="w-10 text-center text-xs font-mono font-semibold text-light-text dark:text-dark-text shrink-0">
                    {student.roll_no || index + 1}
                  </div>

                  {/* Name */}
                  <div className="flex-1 min-w-0 pl-1">
                    <p className="text-sm font-semibold text-light-text dark:text-dark-text whitespace-nowrap overflow-hidden text-ellipsis">{student.student_name}</p>
                    <p className="text-[10px] text-light-text-muted whitespace-nowrap overflow-hidden text-ellipsis">{student.batch_name || ''}{student.batch_name && student.father_phone_1 ? ' • ' : ''}{student.father_phone_1 || ''}</p>
                  </div>

                  {/* Status */}
                  <div className="w-[68px] flex justify-center shrink-0">
                    {moduleKey === 'payments' ? (() => {
                      const ps = paymentStatusMap[student.id];
                      const pStatus = ps?.status || 'no-invoice';
                      const pConfig = pStatus === 'no-invoice'
                        ? { label: 'No Invoice', color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-500/20', icon: Receipt }
                        : (paymentStatusConfig[pStatus] || paymentStatusConfig.due);
                      const PIcon = pConfig.icon;
                      return (
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${pConfig.bg} ${pConfig.color} whitespace-nowrap`}>
                          <PIcon className="w-3 h-3" />
                          <span>{pConfig.label}</span>
                        </span>
                      );
                    })() : (
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${statusInfo.bg} ${statusInfo.color} whitespace-nowrap`}>
                        <StatusIcon className="w-3 h-3" />
                        <span>{statusInfo.label}</span>
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="w-[68px] flex items-center justify-end gap-0.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-lime-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        const numbers = [
                          { label: 'Father', phone: student.father_phone_1, sub: student.father_phone_2 },
                          { label: 'Mother', phone: student.mother_phone_1, sub: student.mother_phone_2 },
                          { label: 'WhatsApp', phone: student.whatsapp_number },
                        ].filter(n => n.phone || n.sub);
                        if (numbers.length === 0) {
                          toast.info('No phone numbers available');
                          return;
                        }
                        setCallNumbers(numbers);
                        setShowCallModal(true);
                      }}
                    >
                      <PhoneCall className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={(e) => { e.stopPropagation(); navigate(`/modules/${moduleKey}/classes/${classId}/batches/${batchId}/students/${student.id}/edit`); }}
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    {moduleKey === 'students' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-rose-500"
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(student.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Call modal */}
      <AlertDialog open={showCallModal} onOpenChange={setShowCallModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-lime-600" /> Call
            </AlertDialogTitle>
            <AlertDialogDescription>Choose a number to call</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2">
            {callNumbers.map((item) => (
              <div key={item.label} className="border border-light-border dark:border-dark-border rounded-xl p-3 space-y-2">
                <p className="text-xs font-semibold text-light-text dark:text-dark-text">{item.label}</p>
                {item.phone && (
                  <a href={`tel:${item.phone}`} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-lime-50 dark:bg-lime-500/10 hover:bg-lime-100 dark:hover:bg-lime-500/20 transition-colors text-sm font-medium text-lime-700 dark:text-lime-300">
                    <Phone className="w-4 h-4" /> {item.phone}
                  </a>
                )}
                {item.sub && item.sub !== item.phone && (
                  <a href={`tel:${item.sub}`} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-lime-50 dark:bg-lime-500/10 hover:bg-lime-100 dark:hover:bg-lime-500/20 transition-colors text-sm font-medium text-lime-700 dark:text-lime-300">
                    <Phone className="w-4 h-4" /> {item.sub} <span className="text-[10px] text-lime-500 ml-auto">Alt</span>
                  </a>
                )}
              </div>
            ))}
            {callNumbers.length === 0 && (
              <p className="text-center text-sm text-light-text-muted py-4">No phone numbers available</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enquiry delete confirmation */}
      <AlertDialog open={!!enquiryDeleteId} onOpenChange={() => setEnquiryDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Enquiry</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => {
              if (enquiryDeleteId) deleteEnquiry.mutate(enquiryDeleteId);
              setEnquiryDeleteId(null);
            }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Student delete confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Student</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this student? All records will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => {
              if (deleteConfirm) deleteStudent.mutate(deleteConfirm);
            }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  // Module-specific top button
  function getTopButton() {
    if (isEnquiries) {
      return <Button size="sm" onClick={() => navigate(`/students/add?classId=${classId}&batchId=${batchId || ''}&enquiry=true`)}><Plus className="h-4 w-4" /> Add Enquiry</Button>;
    }
    if (moduleKey === 'students') {
      return (
        <Button asChild size="sm"><Link to={`/students/add?classId=${classId || ''}&batchId=${batchId || ''}`}><UserPlus className="h-4 w-4" /> Add Student</Link></Button>
      );
    }
    return null;
  }
}
